import { FC, PointerEvent, ReactNode, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface PointerTrackingTooltipProps {
    children: ReactNode;
    content: ReactNode;
    offset?: string;
}

const DEFAULT_TOOLTIP_WIDTH = 13 * 16;
const DEFAULT_TOOLTIP_HEIGHT = 12 * 16;
const VIEWPORT_GUTTER = 16;

const PointerTrackingTooltip: FC<PointerTrackingTooltipProps> = ({ children, content, offset = '0.5rem' }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);
    const [active, setActive] = useState(false);
    const [anchorBottom, setAnchorBottom] = useState(0);
    const [anchorTop, setAnchorTop] = useState(0);
    const [pointerX, setPointerX] = useState(VIEWPORT_GUTTER);
    const [tooltipHeight, setTooltipHeight] = useState(DEFAULT_TOOLTIP_HEIGHT);
    const [tooltipWidth, setTooltipWidth] = useState(DEFAULT_TOOLTIP_WIDTH);

    useLayoutEffect(() => {
        if (!active || !tooltipRef.current) {
            return;
        }

        const bounds = tooltipRef.current.getBoundingClientRect();
        const measuredHeight = bounds.height;
        const measuredWidth = bounds.width;

        if (measuredWidth > 0 && measuredWidth !== tooltipWidth) {
            setTooltipWidth(measuredWidth);
        }

        if (measuredHeight > 0 && measuredHeight !== tooltipHeight) {
            setTooltipHeight(measuredHeight);
        }
    }, [active, tooltipHeight, tooltipWidth]);

    const updatePointer = (event: PointerEvent<HTMLDivElement>) => {
        const bounds = event.currentTarget.getBoundingClientRect();

        setAnchorBottom(bounds.bottom);
        setAnchorTop(bounds.top);
        setPointerX(Math.min(Math.max(event.clientX, bounds.left), bounds.right));
    };

    const centerTooltip = () => {
        const bounds = containerRef.current?.getBoundingClientRect();

        if (!bounds) {
            return;
        }

        setAnchorBottom(bounds.bottom);
        setAnchorTop(bounds.top);
        setPointerX(bounds.left + bounds.width / 2);
        setActive(true);
    };

    const viewportWidth = typeof window === 'undefined' ? tooltipWidth + VIEWPORT_GUTTER * 2 : window.innerWidth;
    const renderedTooltipWidth = Math.min(tooltipWidth, viewportWidth - VIEWPORT_GUTTER * 2);
    const clampedPointerX = Math.min(
        Math.max(pointerX, renderedTooltipWidth / 2 + VIEWPORT_GUTTER),
        viewportWidth - renderedTooltipWidth / 2 - VIEWPORT_GUTTER,
    );
    const roomAbove = anchorTop - VIEWPORT_GUTTER;
    const roomBelow =
        (typeof window === 'undefined' ? Number.POSITIVE_INFINITY : window.innerHeight) -
        anchorBottom -
        VIEWPORT_GUTTER;
    const renderBelow = roomAbove < tooltipHeight && roomBelow > roomAbove;
    const tooltip = active ? (
        <div
            data-pointer-tracking-tooltip='true'
            data-tooltip-placement={renderBelow ? 'bottom' : 'top'}
            ref={tooltipRef}
            role='tooltip'
            style={{
                bottom: renderBelow ? undefined : `${Math.max(0, window.innerHeight - anchorTop)}px`,
                left: `${clampedPointerX}px`,
                marginBottom: renderBelow ? undefined : offset,
                marginTop: renderBelow ? offset : undefined,
                maxHeight: `calc(100vh - ${VIEWPORT_GUTTER * 2}px)`,
                maxWidth: `calc(100vw - ${VIEWPORT_GUTTER * 2}px)`,
                overflow: 'auto',
                pointerEvents: 'none',
                position: 'fixed',
                top: renderBelow ? `${anchorBottom}px` : undefined,
                transform: 'translateX(-50%)',
                zIndex: 1050,
            }}
        >
            {content}
        </div>
    ) : null;

    return (
        <div
            data-pointer-tracking-tooltip-trigger='true'
            ref={containerRef}
            style={{ position: 'relative', width: '100%' }}
            onBlur={() => setActive(false)}
            onFocus={centerTooltip}
            onPointerEnter={(event) => {
                updatePointer(event);
                setActive(true);
            }}
            onPointerLeave={() => setActive(false)}
            onPointerMove={updatePointer}
        >
            {children}
            {tooltip && typeof document !== 'undefined' ? createPortal(tooltip, document.body) : null}
        </div>
    );
};

export default PointerTrackingTooltip;
